import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box } from "braid-design-system";
import { useRef, useState, useEffect, useCallback } from "react";
import { translations } from "../../../components/DisclosureDialog/.vocab/index.mjs";
import { container, summary, detailsWrapper, details } from "./DisclosureDialog.css.mjs";
const DisclosureDialog = ({
  children,
  isOpen: initialIsOpen = false,
  label,
  tooltip,
  onOpenChanged
}) => {
  const { t } = useTranslations(translations);
  const ref = useRef(null);
  const [anchor, setAnchor] = useState("center");
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        var _a, _b;
        const isIntersectingLeft = entry.boundingClientRect.left - entry.boundingClientRect.width / 2 < (((_a = entry.rootBounds) == null ? void 0 : _a.left) || 0);
        if (isIntersectingLeft) {
          setAnchor("left");
        } else {
          const isIntersectingRight = entry.boundingClientRect.right + entry.boundingClientRect.width / 2 > (((_b = entry.rootBounds) == null ? void 0 : _b.right) || 0);
          if (isIntersectingRight) {
            setAnchor("right");
          } else {
            setAnchor("center");
          }
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  useEffect(() => {
    setIsOpen(initialIsOpen);
  }, [initialIsOpen]);
  useEffect(() => {
    if (onOpenChanged) {
      onOpenChanged(isOpen);
    }
    if (isOpen) {
      const handleKeyDown = ({ key }) => {
        if (key === "Escape") {
          setIsOpen(false);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, onOpenChanged]);
  const onTriggerClicked = useCallback((e) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  }, []);
  return /* @__PURE__ */ jsxs(Box, { className: container, position: "relative", open: isOpen, children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        "aria-expanded": isOpen,
        "aria-modal": "false",
        className: summary,
        cursor: "pointer",
        position: "relative",
        role: "button",
        title: isOpen ? t("Click to close dialog") : label ?? t("Click to open dialog"),
        type: "button",
        onClick: onTriggerClicked,
        children: [
          isOpen && /* @__PURE__ */ jsx(
            Box,
            {
              bottom: 0,
              cursor: "default",
              display: "block",
              left: 0,
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: "dropdownBackdrop"
            }
          ),
          children
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Box,
      {
        className: detailsWrapper,
        "data-anchor": anchor,
        display: isOpen ? "block" : "none",
        position: "relative",
        zIndex: "dropdown",
        children: /* @__PURE__ */ jsx(
          Box,
          {
            background: "surface",
            className: details,
            marginTop: "small",
            padding: "medium",
            position: "absolute",
            ref,
            zIndex: "dropdown",
            boxShadow: "large",
            children: tooltip
          }
        )
      }
    )
  ] });
};
export {
  DisclosureDialog
};
